import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Map, Inbox, FileText, LogOut, ExternalLink } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { api } from "../../lib/api";
import { useSite } from "../../lib/SiteContext";
import Seo from "../../components/Seo";
import AdminEnquiries from "./AdminEnquiries";
import AdminTours from "./AdminTours";
import AdminContent from "./AdminContent";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { refresh } = useSite();
  const [stats, setStats] = useState(null);

  const loadStats = async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch {
      localStorage.removeItem("aloha_admin_token");
      navigate("/admin/login");
    }
  };

  useEffect(() => { loadStats(); }, []);

  const logout = () => {
    localStorage.removeItem("aloha_admin_token");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-sand" data-testid="admin-dashboard">
      <Seo title="Admin Dashboard — Aloha Corfu Tours" description="Manage Aloha Corfu Tours website." />
      <header className="sticky top-0 z-40 border-b border-gray-200 bg-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <img src="/assets/aloha-logo.png" alt="Aloha Corfu Tours logo" className="h-9 w-9 rounded-full" />
            <div>
              <p className="font-display text-base font-semibold text-pine leading-tight">Aloha Corfu Tours</p>
              <p className="text-xs text-gray-400">Website Manager</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-50" data-testid="admin-view-site">
              <ExternalLink className="h-3.5 w-3.5" /> View site
            </Link>
            <button onClick={logout} className="inline-flex items-center gap-1.5 rounded-full bg-pine px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-pine-light" data-testid="admin-logout-button">
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        {stats && (
          <div className="mb-8 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="admin-stats">
            {[
              { label: "Tours", value: stats.tours, icon: Map },
              { label: "Total enquiries", value: stats.enquiries, icon: Inbox },
              { label: "New enquiries", value: stats.new_enquiries, icon: LayoutDashboard },
              { label: "Contact messages", value: stats.messages, icon: FileText },
            ].map((s) => (
              <div key={s.label} className="rounded-2xl border border-gray-100 bg-white p-4 shadow-soft">
                <s.icon className="h-5 w-5 text-seafoam-dark" />
                <p className="mt-2 font-display text-2xl font-semibold text-pine">{s.value}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        <Tabs defaultValue="enquiries">
          <TabsList className="mb-6 flex h-auto flex-wrap gap-1 rounded-2xl bg-white p-1.5 shadow-soft" data-testid="admin-tabs">
            <TabsTrigger value="enquiries" className="rounded-xl text-sm data-[state=active]:bg-pine data-[state=active]:text-white" data-testid="tab-enquiries">Enquiries</TabsTrigger>
            <TabsTrigger value="tours" className="rounded-xl text-sm data-[state=active]:bg-pine data-[state=active]:text-white" data-testid="tab-tours">Tours</TabsTrigger>
            <TabsTrigger value="content" className="rounded-xl text-sm data-[state=active]:bg-pine data-[state=active]:text-white" data-testid="tab-content">Site Content</TabsTrigger>
          </TabsList>
          <TabsContent value="enquiries"><AdminEnquiries onChange={loadStats} /></TabsContent>
          <TabsContent value="tours"><AdminTours onSaved={() => { loadStats(); refresh(); }} /></TabsContent>
          <TabsContent value="content"><AdminContent onSaved={refresh} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
